"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import IPTypeBadge from "@/components/ui/IPTypeBadge";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = {
  data: DashboardSummary['urgentAlerts'] | null;
  loading: boolean;
};

const severityConfig = {
  critical: { border: "border-red-200", bg: "bg-red-50", badge: "bg-red-100 text-red-700 border-red-200", icon: "text-red-500", label: "CRITICAL" },
  high: { border: "border-orange-200", bg: "bg-orange-50", badge: "bg-orange-100 text-orange-700 border-orange-200", icon: "text-orange-500", label: "HIGH" },
  medium: { border: "border-yellow-200", bg: "bg-yellow-50/60", badge: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: "text-yellow-600", label: "MEDIUM" },
};

export default function UrgentAlertsPanel({ data, loading }: Props) {
  const [dismissed, setDismissed] = useState<string[]>([]);

  const alerts = (data ?? []).filter((alert) => !dismissed.includes(alert.id));

  if (loading || !data) return <div className="bg-white rounded-xl border border-border shadow-card p-5 h-[420px] animate-pulse" />;

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5 h-full max-h-[500px] flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center">
            <Icon name="BellAlertIcon" size={16} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-[15px] font-600 text-foreground">Urgent Alerts</h3>
            <p className="text-[11px] text-muted-foreground">{alerts.length} requiring attention</p>
          </div>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-8 flex-1 flex flex-col justify-center">
          <div className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-2">
            <Icon name="CheckCircleIcon" size={20} className="text-green-600" />
          </div>
          <p className="text-[13px] font-500 text-foreground">All clear - no urgent alerts</p>
        </div>
      ) : (
        <ul className="space-y-2.5 overflow-y-auto flex-1 pr-1 custom-scrollbar">
          {alerts.map((alert) => {
            const sev = severityConfig[alert.severity];
            return (
              <li key={alert.id} className={`rounded-lg border p-3 transition-all duration-200 ${sev.border} ${sev.bg} group`}>
                <div className="flex items-start gap-2.5">
                  <Icon name="ExclamationTriangleIcon" size={16} className={`mt-0.5 flex-shrink-0 ${sev.icon}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[9px] font-700 tracking-wider px-1.5 py-0.5 rounded border ${sev.badge}`}>{sev.label}</span>
                      <span className="font-mono text-[11px] font-600 text-foreground">{alert.caseNo}</span>
                      <IPTypeBadge type={alert.ipType} size="sm" showIcon={false} />
                    </div>
                    <p className="text-[12px] font-600 text-foreground leading-snug truncate" title={alert.caseTitle}>{alert.caseTitle}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{alert.client}</p>
                    <p className="text-[11px] font-500 text-foreground mt-1.5">{alert.message}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-muted-foreground">{alert.dueDate}</span>
                        <span className="text-[10px] text-muted-foreground">{alert.assignedTo}</span>
                      </div>
                      <button onClick={() => setDismissed((d) => [...d, alert.id])} className="text-[10px] font-500 text-muted-foreground hover:text-foreground transition-colors">Dismiss</button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
